import {
    AddDocRequest,
    EditDocRequest,
    EmployeeDocsFilterRequest,
    EmployeeDocsFilterResponse,
    EmployeePageDataResponse,
    InitResponse,
    Region,
    MessagesFilterRequest,
    MessagesFilterResponse,
    MessageItem,
    ForgotPasswordRequest,
    MarkMessageAsReadResponse,
    LoginRequest,
    LoginResponse,
    LoginByCodeRequest,
} from "@/models/apiModels";
import axiosStatic, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { sleep } from "@/util";
import {
    CreateEmployeeRequest,
    EditEmployeeRequest,
    Employee,
    EmployeeActionsRequest,
    EmployeeActionsResponse,
    EmployeeFilterResource,
    EmployeeFilterResponse,
    EmployeeOrdersFilterRequest,
    EmployeeOrdersFilterResponse,
    ExportEmployeeResponse,
} from "@/models/employees";
import { OrdersFilterRequest, OrdersFilterResponse, OrderDetails, ExportOrdersResponse } from "@/models/orders";
import {
    AccountingFilterRequest,
    AccountingDetailsFilterRequest,
    AccountingFilterResponse,
    InvoiceDetails,
    ReceiptDetails,
} from "@/models/accounting";
import {
    BulkUpdateDigitalWalletsRequest,
    BulkUpdateDigitalWalletsResponse,
    DigitalWalletFilterRequest,
    DigitalWalletFilterResponse,
    DigitalWalletManagementFilterRequest,
    DigitalWalletManagementFilterResponse,
    EmployeeDigitalWalletFilterRequest,
    EmployeeDigitalWalletResponse,
    ExportDigitalWalletResponse,
    SearchDigitalWalletIdsResponse,
} from "@/models/digital.wallet";
import { TransactionFilterRequest, TransactionFilterResponse } from "@/models/transaction";
import {
    ChatMessagesResponse,
    CreateFunDayRequest,
    CreateGroupOrderRequest,
    FormsFilterRequest,
    FormsFilterResponse,
    MarkAsReadRequest,
} from "@/models/forms";
import { DashboardBudgetResponse, DashboardDataResponse } from "@/models/homePage";
import { HomePagePeriod } from "@/models/enums";
import {
    GetTaskInfoResponse,
    GetTaskProgressRequest,
    GetTaskProgressResponse,
    ImportFileReponse,
    ImportValidationResponse,
    NewGroupRequest,
    NewGroupResponse,
    SaveProfileRequest,
    SaveProfileResponse,
    ValidateImportRequest,
} from "@/models/import";
import {
    AddProfileRequest,
    AddProfileResponse,
    CreateUserRequest,
    DeleteProfileRequest,
    DeleteProfileResponse,
    EditProfileRequest,
    FilterProfilesRequest,
    FilterUsersRequest,
    FilterUsersResponse,
    GetUserProfilePermissionsResponse,
    GetUserProfileResponse,
    User,
} from "@/models/user";
import { GetInterfaceTransactionResponse, InterfaceTransactionFilterRequest } from "@/models/interfaces";
import { IdNameResponse, NameResponse } from "@/dto/responses/id.name.response";
import { GetInterfacesResponse, InterfaceFilterRequest } from "@/models/interfaces";
import i18n from "i18next";
import {
    CreateOrganizationDepartmentRequest,
    EditEmployeeGroupRequest,
    EditOrganizationDepartmentRequest,
    EmployeeGroupFilterRequest,
    GetEmployeeGroupsResponse,
    GetOrganizationDepartmentsResponse,
    OrganizationDepartmentFilterRequest,
} from "@/models/departmentGroup";
import swal from "sweetalert2";

const t = i18n.t;
const axios = axiosStatic.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
});

const requestDelayInterceptor = async (request: AxiosRequestConfig) => {
    request.welloDelayPromise = sleep(400);
    return request;
};
const responseDelay_onFulfilled = async (response: AxiosResponse) => {
    await response.config.welloDelayPromise;
    return response;
};
const responseDelay_onRejected = async (error: AxiosError) => {
    await error.config?.welloDelayPromise;
    if (error.response?.status === 401) {
        await swal.fire({
            title: t("validation.error") as string,
            icon: "error",
            text: t("general.Unauthorized") as string,
            customClass: {
                title: "centered-title",
            },
        });
        const returnUrl = encodeURIComponent(window.location.pathname);
        window.location.href = `/login?return=${returnUrl}`;
        return;
    }
    return Promise.reject(error);
};

axios.interceptors.request.use(requestDelayInterceptor);
axios.interceptors.response.use(responseDelay_onFulfilled, responseDelay_onRejected);

class ErrorsParsed {
    private _errors: string[] | null = null;
    private _validationErrors: { [key: string]: string[] } | null = null;
    private _axiosError: AxiosError;
    constructor(axiosError: AxiosError, errors: string[] | null, validationErrors: { [key: string]: string[] } | null) {
        this._axiosError = axiosError;
        this._errors = errors;
        this._validationErrors = validationErrors;
    }

    public get errors() {
        return this._errors;
    }

    public get validationErrors() {
        return this._validationErrors;
    }

    public get axiosError() {
        return this._axiosError;
    }
}

class BackendApi {
    public getDashboardData(orderSummarySelect: HomePagePeriod, totalUsedSelect: HomePagePeriod) {
        return axios.get<DashboardDataResponse>("/api/home-page/get-dashboard-data", {
            params: { orderSummarySelect, totalUsedSelect },
        });
    }

    public getBudgetData(budgetPeriod: HomePagePeriod) {
        return axios.get<DashboardBudgetResponse>("/api/home-page/get-budget-data", { params: { budgetPeriod } });
    }

    public getRegions() {
        return axios.get<Region[]>("/api/data-list/get-regions");
    }

    public createFunDayForm(request: CreateFunDayRequest) {
        return axios.post("/api/forms-chat/create-fun-day-form", request);
    }

    public createGroupOrderForm(request: CreateGroupOrderRequest) {
        return axios.post("/api/forms-chat/create-group-order-form", request);
    }

    public getForms(request: FormsFilterRequest) {
        return axios.post<FormsFilterResponse>("/api/forms-chat/get-forms", request);
    }

    public getChatMessages(formId: number | null) {
        return axios.get<ChatMessagesResponse>("/api/forms-chat/get-chat-messages", { params: { formId } });
    }

    public sendMessage(config: { headers: { "Content-Type": string } }, fd: FormData) {
        return axios.post(`api/forms-chat/send-chat-message`, fd, config);
    }

    public markAsRead(request: MarkAsReadRequest) {
        return axios.post(`api/forms-chat/mark-as-read`, request);
    }
    public authInfo() {
        return axios.get<InitResponse>("/api/auth/info");
    }

    public changeLanguage(lang: string) {
        const request = {
            lang,
        };
        return axios.post("/api/auth/chlang", request);
    }

    public addDoc(request: AddDocRequest) {
        let formData = new FormData();
        for (let e of Object.entries(request)) {
            formData.append(e[0], e[1]);
        }
        return axios.postForm("/api/attached-doc/add-doc", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    }

    public editDoc(request: EditDocRequest) {
        return axios.post("/api/attached-doc/edit-doc", request);
    }

    public generateEmployeeDocLink(docId: number): string {
        return `/api/attached-doc/download-employee-doc/?docId=${docId}`;
    }

    public getEmployeeDocs(request: EmployeeDocsFilterRequest) {
        return axios.post<EmployeeDocsFilterResponse>("/api/attached-doc/get-employee-docs", request);
    }

    public getEmployees(request: EmployeeFilterResource) {
        return axios.post<EmployeeFilterResponse>("/api/employee/get-employees", request);
    }

    public exportEmployees(request: EmployeeFilterResource) {
        return axios.post<ExportEmployeeResponse>("/api/employee/export-employees", request);
    }

    public generateEmployeeExportLink(interfaceId: number) {
        return `/api/employee/download-employees-xlsx/?interfaceId=${interfaceId}`;
    }

    public getInterfaces(request: InterfaceFilterRequest) {
        return axios.post<GetInterfacesResponse>("/api/interfaces/get-interfaces", request);
    }

    public getInterfaceTransactions(request: InterfaceTransactionFilterRequest) {
        return axios.post<GetInterfaceTransactionResponse>("/api/interfaces/get-interface-transactions", request);
    }

    public getEmployeeOrders(request: EmployeeOrdersFilterRequest) {
        return axios.post<EmployeeOrdersFilterResponse>("/api/employee/get-employee-orders", request);
    }

    public createEmployee(request: CreateEmployeeRequest) {
        return axios.post("/api/employee/create-employee", request);
    }

    public getEmployeeData(id: number | null) {
        return axios.get<Employee>("/api/employee/get-employee-data/", { params: { id } });
    }

    public getEmployeesPageData() {
        return axios.get<EmployeePageDataResponse>("/api/data-list/get-employees-page-data");
    }

    public getDigitalWallets(request: DigitalWalletFilterRequest) {
        return axios.post<DigitalWalletFilterResponse>("/api/digital-wallets", request);
    }

    public getDigitalWalletsManagement(request: DigitalWalletManagementFilterRequest) {
        return axios.post<DigitalWalletManagementFilterResponse>("/api/digital-wallet/management", request);
    }

    public searchDigitalWalletIds(request: DigitalWalletManagementFilterRequest) {
        return axios.post<SearchDigitalWalletIdsResponse>("/api/digital-wallet/searchIds", request);
    }

    public exportDigitalWallets(request: DigitalWalletManagementFilterRequest) {
        return axios.post<ExportDigitalWalletResponse>("/api/digital-wallet/export", request);
    }

    public generateDigitalWalletsExportLink(interfaceId: number) {
        return `/api/digital-wallet/download-digital-wallets-csv/?interfaceId=${interfaceId}`;
    }

    public bulkUpdateDigitalWallets(request: BulkUpdateDigitalWalletsRequest) {
        return axios.post<BulkUpdateDigitalWalletsResponse>("/api/digital-wallet/bulk-update", request);
    }

    public getEmployeeDigitalWallet(request: EmployeeDigitalWalletFilterRequest) {
        return axios.post<EmployeeDigitalWalletResponse>("/api/digital-wallet/employee-digital-wallet", request);
    }

    public getTransactions(request: TransactionFilterRequest) {
        return axios.post<TransactionFilterResponse>("/api/transactions", request);
    }

    public getAllTransactions(request: TransactionFilterRequest) {
        return axios.post<TransactionFilterResponse>("/api/all-transactions", request);
    }

    public editEmployee(request: EditEmployeeRequest) {
        return axios.post("/api/employee/edit-employee", request);
    }

    public employeeActions(request: EmployeeActionsRequest) {
        return axios.post<EmployeeActionsResponse>("/api/employee/employee-actions", request);
    }

    public getAccountingDocuments(request: AccountingFilterRequest) {
        return axios.post<AccountingFilterResponse>("/api/accounting-documents", request);
    }

    public getInvoiceDetails(request: AccountingDetailsFilterRequest) {
        return axios.post<InvoiceDetails>("/api/invoice-details", request);
    }

    public getReceiptDetails(request: AccountingDetailsFilterRequest) {
        return axios.post<ReceiptDetails>("/api/receipt-details", request);
    }

    public getOrders(request: OrdersFilterRequest) {
        return axios.post<OrdersFilterResponse>("/api/orders", request);
    }

    public getOrder(id: string | undefined) {
        return axios.get<OrderDetails>("/api/order/", { params: { Id: id } });
    }

    public exportOrders(request: OrdersFilterRequest) {
        return axios.post<ExportOrdersResponse>("/api/order/export-orders", request);
    }

    public generateOrdersExportLink(interfaceId: number) {
        return `/api/order/download-orders-xlsx/?interfaceId=${interfaceId}`;
    }

    public getMessages(request: MessagesFilterRequest) {
        return axios.post<MessagesFilterResponse>("/api/messages", request);
    }

    public getMessage(id: number) {
        return axios.get<MessageItem>("/api/message", { params: { id: id } });
    }

    public saveMessage(message: MessageItem) {
        return axios.post<number>("/api/message", { Message: message });
    }

    public markMessageAsRead(id: number) {
        return axios.post<MarkMessageAsReadResponse>("/api/message/mark-as-read", { id });
    }
    public importUploadExcel(f: FormData) {
        return axios.post<ImportFileReponse>("/api/upload-excel", f);
    }

    public queueImportProgress(param: GetTaskProgressRequest) {
        return axios.get<GetTaskProgressResponse>("/api/queue/progress", { params: param });
    }
    public getTaskInfo(taskId: string) {
        return axios.get<GetTaskInfoResponse>("/api/upload-excel/task-info", { params: { TaskId: taskId } });
    }
    public importValidate(req: ValidateImportRequest) {
        return axios.post<ImportValidationResponse>("/api/upload-excel/validate", req);
    }
    public importStart(taskId: string) {
        return axios.post<GetTaskInfoResponse>("/api/upload-excel/import", { taskId });
    }
    public saveImportProfile(saveProfile: SaveProfileRequest) {
        return axios.post<SaveProfileResponse>("/api/crm/saveimportprofile", saveProfile);
    }

    public createUser(request: CreateUserRequest) {
        return axios.post("/api/user/create-user", request);
    }

    public getUsers(request: FilterUsersRequest) {
        return axios.post<FilterUsersResponse>("/api/users", request);
    }

    public getUserInfo(id: number) {
        return axios.get<User>("/api/user/get-user", { params: { id } });
    }

    public editUser(request: User) {
        return axios.post<User>("/api/user/edit-user", request);
    }

    public deleteUser(userId: number) {
        return axios.post<number>("/api/user/delete-user", { userId });
    }
    public createBoUser(request: CreateUserRequest) {
        return axios.post("/api/user/create-bo-user", request);
    }

    public getBoUsers(request: FilterUsersRequest) {
        return axios.post<FilterUsersResponse>("/api/bo-users", request);
    }

    public getBoUserInfo(id: number) {
        return axios.get<User>("/api/user/get-bo-user", { params: { id } });
    }

    public editBoUser(request: User) {
        return axios.post<User>("/api/user/edit-bo-user", request);
    }

    public deleteBoUser(userId: number) {
        return axios.post<number>("/api/user/delete-bo-user", { userId });
    }

    public getUserGroups() {
        return axios.get<IdNameResponse>("/api/user/get-user-groups");
    }
    //Permissions apis
    public getUserProfiles(req: FilterProfilesRequest) {
        return axios.post<GetUserProfileResponse>("/api/user/get-user-profiles", req);
    }

    public addUserProfile(request: AddProfileRequest) {
        return axios.post<AddProfileResponse>("/api/user/add-user-profile", request);
    }

    public editUserProfile(request: EditProfileRequest) {
        return axios.post<AddProfileResponse>("/api/user/edit-user-profile", request);
    }

    public deleteUserProfile(request: DeleteProfileRequest) {
        return axios.post<DeleteProfileResponse>("/api/user/delete-user-profile", request);
    }

    public getUserProfilePermissions(profileId: number) {
        return axios.get<GetUserProfilePermissionsResponse>("/api/user/get-user-permissions", {
            params: { profileId },
        });
    }

    public autocompleteProviders(name: string) {
        return axios.get<NameResponse>("/api/order/search-providers", {
            params: { name: name },
        });
    }

    public login(request: LoginRequest) {
        return axios.post<LoginResponse>("/api/auth/login", request);
    }

    public loginByCode(request: LoginByCodeRequest) {
        return axios.post("/api/auth/login-2fa", request);
    }

    public logout() {
        return axios.get("/api/auth/logout");
    }

    public forgotPassword(request: ForgotPasswordRequest) {
        return axios.post<boolean>("/api/auth/forgot-password", request);
    }

    public easyLogin(payload: string) {
        const data = { payload: payload };
        return axios.post("/api/auth/easy-login", data);
    }

    public createOrganizationDepartment(request: CreateOrganizationDepartmentRequest) {
        return axios.post("/api/department-groups/create-organization-department", request);
    }

    public editOrganizationDepartment(request: EditOrganizationDepartmentRequest) {
        return axios.post("/api/department-groups/edit-organization-department", request);
    }

    public getOrganizationDepartments(request: OrganizationDepartmentFilterRequest) {
        return axios.post<GetOrganizationDepartmentsResponse>(
            "/api/department-groups/get-organization-departments",
            request
        );
    }

    public getEmployeeGroups(request: EmployeeGroupFilterRequest) {
        return axios.post<GetEmployeeGroupsResponse>("/api/department-groups/get-employee-groups", request);
    }

    public addNewGroup(req: NewGroupRequest) {
        return axios.post<NewGroupResponse>("/api/department-groups/create-employee-group", req);
    }

    public editEmployeeGroup(request: EditEmployeeGroupRequest) {
        return axios.post("/api/department-groups/edit-employee-group", request);
    }

    public downloadFormPdf(formId: number) {
        return axios.get("/api/forms-chat/download-form-pdf", {
            responseType: "arraybuffer",
            params: { formId: formId },
        });
    }

    public downloadInvoiceDocumentPdf(id: number) {
        return axios.get("/api/invoice/generate-pdf", {
            responseType: "arraybuffer",
            params: { id: id },
        });
    }

    public parseFileName(axiosResponse: AxiosResponse) {
        let fileName = null;
        const disposition = axiosResponse.headers["content-disposition"];
        if (disposition && disposition.indexOf("attachment") !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
                fileName = matches[1].replace(/['"]/g, "");
            }
        }
        return fileName;
    }

    public parseAxiosError(err: unknown) {
        const self = this;
        if (!axiosStatic.isAxiosError(err)) {
            // Should never happen
            throw new Error("Must be AxiosError");
        }
        const axiosErr = err as AxiosError;
        let errors: string[] | null;
        if (axiosErr.response) {
            const status = axiosErr.response.status;
            switch (status) {
                // 400. Bad Request
                case 400:
                    return self.parseStatusCode400(axiosErr);
                // 401. UnAuthorized
                case 401:
                    return self.parseStatusCode401(axiosErr);
                // 403. Permission denied
                case 403:
                    return self.parseStatusCode403(axiosErr);
                // 422. App Error
                case 422:
                    return self.parseStatusCode422(axiosErr);
                // 500. Server Error
                case 500:
                    return self.parseStatusCode500(axiosErr);
                default:
                    errors = [`Unknown error. HTTP Status code ${status}`];
                    return new ErrorsParsed(axiosErr, errors, null);
            }
        } else if (axiosErr.request) {
            errors = ["Network error"];
            return new ErrorsParsed(axiosErr, errors, null);
        } else {
            errors = ["Axios request error"];
            return new ErrorsParsed(axiosErr, errors, null);
        }
    }

    private parseStatusCode400(axiosErr: AxiosError) {
        const data = axiosErr.response!.data as any;
        let validationErrors: { [key: string]: string[] } | null;
        if (data && data.errors) {
            validationErrors = data.errors;
        } else {
            validationErrors = { _: ["Bad Request"] };
        }
        return new ErrorsParsed(axiosErr, null, validationErrors);
    }
    private parseStatusCode401(axiosErr: AxiosError) {
        return new ErrorsParsed(axiosErr, [t("general.Unauthorized")], null);
    }
    private parseStatusCode403(axiosErr: AxiosError) {
        return new ErrorsParsed(axiosErr, ["No Permission"], null);
    }

    private parseStatusCode422(axiosErr: AxiosError) {
        let errors: string[] | null;
        const data = axiosErr.response!.data as any;
        if (data && data.errors) {
            errors = data.errors;
        } else {
            // Should never happen
            errors = ["Error 422. No text"];
        }
        return new ErrorsParsed(axiosErr, errors, null);
    }

    private parseStatusCode500(axiosErr: AxiosError) {
        return new ErrorsParsed(axiosErr, ["Server Error"], null);
    }

    public getFirstError(ex: unknown): [string, number] {
        const self = this;
        const parsed = self.parseAxiosError(ex);
        let errorMessage: string | undefined | null;
        // Try to get first Error
        errorMessage = parsed.errors?.at(0);

        if (errorMessage) {
            return [errorMessage, 1];
        }
        // Try to get first Validation Error
        errorMessage = parsed.validationErrors && Object.values(parsed.validationErrors).at(0)?.at(0);
        if (errorMessage) {
            return [errorMessage, 2];
        }
        // !!Should never happen
        errorMessage = "Unknown error. Could not determine error source";
        return [errorMessage, 1];
    }
}

export default new BackendApi();
